import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowLeft, ExternalLink } from 'lucide-react';
import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import {
  getModel, getDecision, getTests, getFilterSet, getOffer, getSource, getLegacy, METRIC_LABEL, SOURCE_TYPE_LABEL, Source,
} from '@/data/catalogue';
import { fmt, toM3h, cfm, AHAM_ROOM_RATING_EACH, GENERAL_TARGET } from '@/lib/calculator';
import { annualElectricity, DEFAULT_COSTS } from '@/lib/matching';

function SourceLink({ s }: { s?: Source }) {
  if (!s) return <span className="text-muted-foreground">no source</span>;
  return (
    <a href={s.url} target="_blank" rel="noopener noreferrer nofollow" className="text-primary underline inline-flex items-center gap-1">
      {s.publisher} <ExternalLink className="h-3 w-3" />
    </a>
  );
}

/** Floor area (ft², 8 ft ceiling) a given CFM covers at a given eACH */
const coverage = (c: number, each: number) => Math.floor((c * 60) / (each * 8));

export default function ProductPage() {
  const { id = '' } = useParams<{ id: string }>();
  const model = getModel(id);
  const legacy = getLegacy(id);

  if (!model) {
    const succ = legacy?.successorId ? getModel(legacy.successorId) : undefined;
    return (
      <>
        <Helmet><title>{legacy ? `${legacy.name} — record withdrawn` : 'Product not found'}</title><meta name="robots" content="noindex" /></Helmet>
        <Header />
        <main className="container mx-auto px-4 py-16 max-w-2xl">
          <h1 className="text-2xl font-bold text-foreground">{legacy ? `${legacy.name}: record withdrawn` : 'Product not found'}</h1>
          {legacy && <p className="text-muted-foreground mt-4">{legacy.reason}</p>}
          {succ && <p className="mt-4">See the current record: <Link to={`/products/${succ.id}`} className="text-primary underline">{succ.brand} {succ.name}</Link></p>}
          {legacy?.links.map((l) => <p key={l.url} className="mt-2"><a href={l.url} className="text-primary underline" target="_blank" rel="noopener noreferrer">{l.label}</a></p>)}
          <p className="mt-6 text-sm text-muted-foreground">Full history on the <Link to="/corrections" className="text-primary underline">corrections page</Link>.</p>
          <Link to="/products" className="text-primary hover:underline mt-6 inline-block">← Back to the catalogue</Link>
        </main>
        <Footer />
      </>
    );
  }

  const decision = getDecision(model.id)!;
  const tests = getTests(model.id);
  const fs = getFilterSet(model.id);
  const offer = getOffer(model.id);
  const elec = model.ratedPowerW != null ? annualElectricity(model.ratedPowerW, 1, DEFAULT_COSTS) : null;
  const particle = tests.filter((t) => ['smoke', 'dust', 'pollen', 'unspecified'].includes(t.metric) && t.isMaxSpeed);
  const bestForSizing = particle.find((t) => t.metric === 'smoke') ?? particle.find((t) => t.metric === 'dust') ?? particle.find((t) => t.metric === 'unspecified');

  return (
    <>
      <Helmet>
        <title>{`${model.brand} ${model.name}: sourced specs and status`}</title>
        <meta name="description" content={`${model.brand} ${model.name} (${model.sku}): output figures with sources, power, filters and catalogue status (${decision.state}).`} />
        <link rel="canonical" href={`https://whatsizeairpurifier.com/products/${model.id}`} />
        {decision.state === 'archived' && <meta name="robots" content="noindex" />}
      </Helmet>
      <Header />
      <main className="container mx-auto px-4 py-8 max-w-4xl">
        <nav aria-label="Breadcrumb" className="flex items-center gap-2 text-sm text-muted-foreground mb-6">
          <Link to="/" className="hover:text-foreground">Home</Link><span>/</span>
          <Link to="/products" className="hover:text-foreground">Catalogue</Link><span>/</span>
          <span className="text-foreground">{model.name}</span>
        </nav>

        <div className="grid md:grid-cols-[220px_1fr] gap-8 mb-10">
          <div className="rounded-xl p-6 flex items-center justify-center border border-border bg-card">
            {model.image === '/placeholder.svg'
              ? <p className="text-sm text-muted-foreground text-center">No photo of this exact model</p>
              : <img src={model.image} alt={`${model.brand} ${model.name}`} className="max-h-48 w-auto object-contain" onError={(e) => { e.currentTarget.replaceWith(Object.assign(document.createElement('p'), { textContent: 'Photo unavailable', className: 'text-sm text-muted-foreground' })); }} />}
          </div>
          <div>
            <p className="text-sm text-muted-foreground">{model.brand} · {model.sku}{model.generation ? ` · ${model.generation}` : ''}</p>
            <h1 className="text-3xl font-bold text-foreground mt-1">{model.brand} {model.name}</h1>
            <div className="flex flex-wrap gap-2 mt-3">
              <Badge variant={decision.state === 'eligible' ? 'default' : 'outline'}>
                {decision.state === 'eligible' ? 'Evidence accepted' : decision.state === 'held' ? 'Held for review' : 'Archived'}
              </Badge>
              <Badge variant="outline">Model status: {model.status}</Badge>
              <Badge variant="outline">Purifier stock: {offer ? (offer.stock === 'unknown' ? 'not confirmed' : offer.stock.replace(/-/g, ' ')) : 'no US offer'}</Badge>
              <Badge variant="outline">Filter stock: {fs ? (fs.availability === 'unknown' ? 'not confirmed' : fs.availability.replace(/-/g, ' ')) : 'no record'}</Badge>
            </div>
            <p className="text-muted-foreground mt-4">{model.summary}</p>
            <p className="text-sm text-muted-foreground mt-2"><strong className="text-foreground">Catalogue status:</strong> {decision.reason} (sources checked {decision.reviewed})</p>
            {decision.state === 'eligible' && (offer?.stock !== 'in-stock' || fs?.availability !== 'in-stock') && <p className="text-sm mt-2">Not currently shown as a confirmed pick in calculator results, because purifier or filter stock was not confirmed when checked.</p>}
            {decision.successorId && <p className="text-sm mt-2">Successor: <Link className="text-primary underline" to={`/products/${decision.successorId}`}>{getModel(decision.successorId)?.name}</Link></p>}
          </div>
        </div>

        <section className="mb-10">
          <h2 className="text-xl font-bold text-foreground mb-3">Performance figures</h2>
          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="w-full text-sm">
              <thead className="bg-muted"><tr>
                <th scope="col" className="text-left p-3">Metric</th><th scope="col" className="text-left p-3">Value</th>
                <th scope="col" className="text-left p-3">Speed / configuration</th><th scope="col" className="text-left p-3">Noise at that speed</th>
                <th scope="col" className="text-left p-3">Source</th>
              </tr></thead>
              <tbody>
                {tests.length === 0 && <tr><td className="p-3" colSpan={5}>No output figure on record.</td></tr>}
                {tests.map((t) => {
                  const s = getSource(t.sourceId);
                  return (
                    <tr key={t.id} className="border-t border-border align-top">
                      <td className="p-3">{METRIC_LABEL[t.metric]}</td>
                      <td className="p-3">{t.cfm} CFM ({fmt(toM3h(cfm(t.cfm)), 0)} m³/h)</td>
                      <td className="p-3">{t.speed}; {t.filterConfig}; ionizer {t.ionizerState.replace('-', ' ')}</td>
                      <td className="p-3">{t.noiseDbA != null ? `${t.noiseDbA} dBA (${t.noiseMethod})` : 'unknown'}</td>
                      <td className="p-3"><SourceLink s={s} /><div className="text-xs text-muted-foreground">{s ? `${SOURCE_TYPE_LABEL[s.type]}, read ${s.accessed}${s.checked === 'from-audit-notes' ? ', from audit notes' : ''}` : ''}</div>{t.note && <div className="text-xs text-muted-foreground">{t.note}</div>}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {bestForSizing && (
            <p className="text-sm text-muted-foreground mt-3">
              Using the {METRIC_LABEL[bestForSizing.metric].toLowerCase()} of {bestForSizing.cfm} CFM with an 8 ft ceiling: about {coverage(bestForSizing.cfm, GENERAL_TARGET.each)} ft² at this site's {GENERAL_TARGET.each} eACH default, or {coverage(bestForSizing.cfm, AHAM_ROOM_RATING_EACH)} ft² on AHAM's {AHAM_ROOM_RATING_EACH} ACH basis. That is at maximum speed.
            </p>
          )}
          {model.noiseRange && (
            <p className="text-sm text-muted-foreground mt-2">
              Manufacturer noise range: {model.noiseRange.minDb ?? '?'}–{model.noiseRange.maxDb ?? '?'} dB (<SourceLink s={getSource(model.noiseRange.sourceId)} />). A range does not say how much air it moves at the quiet end.
            </p>
          )}
        </section>

        <section className="mb-10 grid sm:grid-cols-2 gap-4">
          <Card><CardContent className="p-5 text-sm space-y-2">
            <h2 className="font-bold text-foreground text-base">Power and running cost</h2>
            <p>Rated power: {model.ratedPowerW != null ? <>{model.ratedPowerW} W (<SourceLink s={getSource(model.ratedPowerSourceId)} />)</> : 'unknown'}</p>
            <p>Electricity at rated power, {DEFAULT_COSTS.hoursPerDay} h/day, ${DEFAULT_COSTS.tariffUsdPerKwh}/kWh: {elec != null ? `~$${fmt(elec, 0)}/yr (upper estimate)` : 'unknown'}</p>
            <p>Ionizer: {model.ionizer.replace(/-/g, ' ')}{model.ionizerNote ? ` — ${model.ionizerNote}` : ''}</p>
            {model.dimensions && <p>Size: {model.dimensions}</p>}
            {model.voltage && <p>Supply: {model.voltage}</p>}
          </CardContent></Card>
          <Card><CardContent className="p-5 text-sm space-y-2">
            <h2 className="font-bold text-foreground text-base">Filters and price</h2>
            {fs ? (
              <>
                <p>{fs.retailName}{fs.sku ? ` (SKU ${fs.sku})` : ''}: {fs.contents}. Lasts {fs.cycleMonths ?? '?'} months per set. Price {fs.priceUsd != null ? `$${fs.priceUsd}` : 'unknown'}{fs.listPriceUsd ? ` (list $${fs.listPriceUsd})` : ''}. Availability: {fs.availability.replace(/-/g, ' ')}{fs.observed ? ` on ${fs.observed}` : ''}{fs.sourceId ? <> (<SourceLink s={getSource(fs.sourceId)} />)</> : ''}.</p>
                <p>Fits: {fs.compatibleWith.length ? fs.compatibleWith.join(', ') : 'not confirmed'}. Filters included with the purifier last {fs.initialIncludedMonths ?? '?'} months.</p>
              </>
            ) : <p>No filter record.</p>}
            {fs?.note && <p className="text-muted-foreground">{fs.note}</p>}
            <p>Observed price: {offer ? <>${fmt(offer.priceUsd, 2)}{offer.listPriceUsd ? ` (list $${offer.listPriceUsd})` : ''} at <a href={offer.url} target="_blank" rel="noopener noreferrer nofollow" className="text-primary underline">{offer.seller}</a> on {offer.observed}</> : 'unknown'}</p>
            <p className="text-xs text-muted-foreground">Not an affiliate link. We earn nothing from it.</p>
          </CardContent></Card>
        </section>

        {decision.conflicts.length > 0 && (
          <section className="mb-10">
            <h2 className="text-xl font-bold text-foreground mb-2">Open questions and conflicts</h2>
            <ul className="list-disc pl-5 text-sm text-muted-foreground space-y-1">{decision.conflicts.map((c) => <li key={c}>{c}</li>)}</ul>
          </section>
        )}

        {model.thirdPartyLinks && (
          <section className="mb-10">
            <h2 className="text-xl font-bold text-foreground mb-2">Elsewhere</h2>
            <ul className="text-sm space-y-1">{model.thirdPartyLinks.map((l) => <li key={l.url}><a href={l.url} target="_blank" rel="noopener noreferrer nofollow" className="text-primary underline">{l.label}</a> <span className="text-muted-foreground">— {l.note}</span></li>)}</ul>
          </section>
        )}

        <p className="text-sm text-muted-foreground mb-6">
          Something wrong? <Link to={`/corrections?model=${model.id}`} className="text-primary underline">Report a correction for this model</Link>.
        </p>
        <Link to="/products" className="inline-flex items-center gap-2 text-primary hover:underline mb-8"><ArrowLeft className="h-4 w-4" /> Back to the catalogue</Link>
      </main>
      <Footer />
    </>
  );
}
